import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { showError, showWarning } from '../utils/sweetAlert';
import logger from '../utils/logger';

const EMHAResults = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingDetail, setDownloadingDetail] = useState(false);
  const [isPaid, setIsPaid] = useState(false);

  useEffect(() => {
    fetchResults();
    checkPaymentStatus();
  }, [assessmentId]);

  const fetchResults = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/emha/results/${assessmentId}`,
        { withCredentials: true }
      );
      setResults(response.data);
    } catch (error) {
      logger.error('Error fetching EMHA results:', error);
      // Only show mock results if it's a 404, not for auth errors
      if (error.response?.status === 404) {
        setResults(createMockResults());
      } else {
        // For other errors (401, 500, etc), show error message
        showError('Error Loading Results', error.response?.data?.detail || 'Failed to load assessment results. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/emha/payment-status/${assessmentId}`,
        { withCredentials: true }
      );
      setIsPaid(response.data.paid || false);
    } catch (error) {
      logger.error('Error checking payment status:', error);
      setIsPaid(false);
    }
  };

  const createMockResults = () => {
    return {
      assessment_id: assessmentId,
      facility_info: {
        companyName: 'Demo Company',
        contactName: 'Demo User',
        email: 'demo@example.com',
        annualEnergySpend: '500000',
        industry: 'Manufacturing'
      },
      scores: {
        overall: 2.3,
        leap_phase: 'Monitor',
        categories: {
          'Leadership & Strategy': 1.8,
          'Policy & Planning': 2.1,
          'Structure & Procedures': 2.5,
          'Regulatory & Financial': 1.9,
          'Measurement & Data': 2.8,
          'Innovation': 2.0,
          'Communication': 2.4,
          'Culture & Training': 2.7
        }
      },
      completed_at: new Date().toISOString()
    };
  };

  const getLeapPhaseInfo = (score) => {
    if (score < 1) return { phase: 'Measure', color: '#ef4444', description: 'Initial energy awareness stage' };
    if (score < 2) return { phase: 'Meter', color: '#f97316', description: 'Basic energy monitoring in place' };
    if (score < 3) return { phase: 'Monitor', color: '#eab308', description: 'Systematic energy monitoring and reporting' };
    if (score < 4) return { phase: 'Manage', color: '#22c55e', description: 'Active energy management with formal procedures' };
    return { phase: 'Maximise', color: '#10b981', description: 'Optimized energy management with continuous improvement' };
  };

  const prepareRadarData = () => {
    if (!results?.scores?.categories) return [];
    const categoryOrder = [
      'Leadership & Strategy',
      'Structure & Procedures', 
      'Regulatory Compliance & Financial Incentives',
      'Energy Management Information System',
      'Product and Process Innovation',
      'Internal Communication',
      'Culture'
    ];
    return categoryOrder.map((category) => {
      const score = results.scores.categories[category] || 0;
      let displayLabel = category;
      switch (category) {
        case 'Leadership & Strategy':
          displayLabel = 'Leadership';
          break;
        case 'Structure & Procedures':
          displayLabel = 'Structure';
          break;
        case 'Regulatory Compliance & Financial Incentives':
          displayLabel = 'Compliance & Finance';
          break;
        case 'Energy Management Information System':
          displayLabel = 'Information Systems';
          break;
        case 'Product and Process Innovation':
          displayLabel = 'Innovation';
          break;
        case 'Internal Communication':
          displayLabel = 'Communication';
          break;
        case 'Culture':
          displayLabel = 'Culture';
          break;
        default:
          displayLabel = category;
      }
      return {
        category: displayLabel,
        fullCategory: category,
        score: score,
        fullMark: 4
      };
    });
  };

  const handleDownloadBasicReport = async () => {
    try {
      setDownloading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/emha/report/basic`,
        { assessment_id: assessmentId },
        { 
          responseType: 'blob',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `EMHA_Basic_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('Error downloading basic report:', error);
      showError('Download Failed', 'Failed to download report. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadDetailedReport = async () => {
    try {
      setDownloadingDetail(true);
      
      // Check if already paid
      const paymentCheckResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/emha/payment-status/${assessmentId}`,
        { withCredentials: true }
      );

      if (paymentCheckResponse.data.paid) {
        // Already paid - download directly
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/emha/report/detailed`,
          { assessment_id: assessmentId },
          { 
            responseType: 'blob',
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true
          }
        );
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `EMHA_Detailed_Report_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        // Not paid - redirect to checkout
        try {
          const checkoutResponse = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout/session`,
            {
              package_id: 'emha_detailed_report',
              assessment_id: assessmentId,
              customer_email: results.facility_info.email,
              origin_url: window.location.origin
            },
            { withCredentials: true }
          );

          if (checkoutResponse.data.checkout_url) {
            // Redirect to Stripe checkout
            // Note: Console warnings from Stripe's checkout page (preload links, module loading, etc.)
            // are expected and harmless - they don't affect payment functionality
            logger.log('Redirecting to Stripe checkout...');
            window.location.href = checkoutResponse.data.checkout_url;
          } else {
            showError('Checkout Failed', 'Unable to create checkout session. Please try again.');
          }
        } catch (checkoutError) {
          logger.error('Error creating checkout session:', checkoutError);
          showError(
            'Checkout Error', 
            checkoutError.response?.data?.detail || 'Failed to create checkout session. Please try again.'
          );
        }
      }
    } catch (error) {
      logger.error('Error processing detailed report request:', error);
      if (error.response?.status === 401) {
        showError('Authentication Required', 'Please sign in to purchase the detailed report.');
        navigate('/login');
      } else {
        showError('Error', error.response?.data?.detail || 'Failed to process request. Please try again.');
      }
    } finally {
      setDownloadingDetail(false);
    }
  };

  const handleUpgradeClick = () => {
    setShowUpgrade(true);
  };

  const handleDownloadReport = async () => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/emha/generate-report`, {
        assessment_id: assessmentId,
        type: 'basic'
      }, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `EMHA-Report-${results.facility_info.companyName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      logger.error('Error downloading report:', error);
      showWarning('Coming Soon', 'Report generation coming soon!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-emerald-900 dark:to-blue-900 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 dark:border-emerald-400 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading your EMHA results...</p>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-emerald-900 dark:to-blue-900 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
          <p className="text-gray-600 dark:text-gray-300">Assessment results not found.</p>
          <button 
            onClick={() => navigate('/dashboard')}
            className="mt-4 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const leapPhase = getLeapPhaseInfo(results.scores.overall);
  const radarData = prepareRadarData();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-emerald-900 dark:to-blue-900 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 mb-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Energy Management Health Assessment Results
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
              {results.facility_info.companyName} - {results.facility_info.industry}
            </p>
            {/* Overall Score */}
            <div className="bg-gradient-to-r from-emerald-500 to-blue-500 rounded-2xl p-8 text-white mb-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
                <div className="text-center">
                  <div className="text-5xl font-bold mb-2">
                    {results.scores.overall.toFixed(1)}
                  </div>
                  <div className="text-lg opacity-90">Overall Score</div>
                  <div className="text-sm opacity-75">out of 4.0</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold mb-2" style={{ color: leapPhase.color }}>
                    {leapPhase.phase}
                  </div>
                  <div className="text-lg opacity-90">LEAP Phase</div>
                  <div className="text-sm opacity-75">{leapPhase.description}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold mb-2">
                    {Math.round((results.scores.overall / 4) * 100)}%
                  </div>
                  <div className="text-lg opacity-90">Maturity Level</div>
                  <div className="text-sm opacity-75">of maximum potential</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Radar Chart and Category Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Radar Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">
              Energy Management Strengths Profile
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 text-center">
              Abbreviated labels shown for clarity. See detailed breakdown for full category names.
            </p>
            <div style={{ width: '100%', height: 400 }}>
              <ResponsiveContainer>
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis 
                    dataKey="category"
                    className="text-sm font-medium"
                    tick={{ fontSize: 13, textAnchor: 'middle', fontWeight: '500' }}
                  />
                  <PolarRadiusAxis angle={90} domain={[0, 4]} tick={{ fontSize: 10 }} />
                  <Radar
                    name="Score"
                    dataKey="score"
                    stroke="#10b981"
                    fill="#10b981"
                    fillOpacity={0.3}
                    strokeWidth={2}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            {/* Legend for abbreviated labels */}
            <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 space-y-1">
              <div className="grid grid-cols-1 gap-1">
                <div><strong>Leadership:</strong> Leadership & Strategy</div>
                <div><strong>Structure:</strong> Structure & Procedures</div>
                <div><strong>Compliance & Finance:</strong> Regulatory Compliance & Financial Incentives</div>
                <div><strong>Information Systems:</strong> Energy Management Information System</div>
                <div><strong>Innovation:</strong> Product and Process Innovation</div>
                <div><strong>Communication:</strong> Internal Communication</div>
                <div><strong>Culture:</strong> Culture</div>
              </div>
            </div>
          </div>

          {/* Category Scores */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Detailed Category Scores
            </h2>
            <div className="space-y-4">
              {[
                { name: 'Leadership & Strategy', questions: 3 },
                { name: 'Structure & Procedures', questions: 5 }, 
                { name: 'Regulatory Compliance & Financial Incentives', questions: 2 },
                { name: 'Energy Management Information System', questions: 3 },
                { name: 'Product and Process Innovation', questions: 2 },
                { name: 'Internal Communication', questions: 2 },
                { name: 'Culture', questions: 3 }
              ].map((categoryInfo) => {
                const score = results.scores.categories[categoryInfo.name] || 0;
                const totalPossible = categoryInfo.questions * 4;
                const actualTotal = score * categoryInfo.questions;
                const percentage = (score / 4) * 100;
                const phaseInfo = getLeapPhaseInfo(score);
                return (
                  <div key={categoryInfo.name} className="border-b border-gray-100 dark:border-gray-700 pb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-gray-900 dark:text-white">{categoryInfo.name}</span>
                      <div className="text-right">
                        <div className="text-lg font-bold text-gray-900 dark:text-white">
                          {actualTotal.toFixed(1)} / {totalPossible}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Avg: {score.toFixed(1)} / 4.0
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-3">
                        <div 
                          className="h-2 rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%`, backgroundColor: phaseInfo.color }}
                        ></div>
                      </div>
                      <span 
                        className="text-sm font-medium px-2 py-1 rounded-full"
                        style={{ backgroundColor: phaseInfo.color + '20', color: phaseInfo.color }}
                      >
                        {phaseInfo.phase}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Next Steps
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Choose how you'd like to proceed with your energy management journey
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Basic Report</h3>
                <div className="text-3xl font-bold text-emerald-600 mb-2">FREE</div>
                <ul className="text-left text-gray-600 dark:text-gray-400 space-y-2 mb-6">
                  <li>✅ Overall maturity score & LEAP phase</li>
                  <li>✅ Category breakdown with radar chart</li>
                  <li>✅ Basic improvement recommendations</li>
                  <li>✅ Executive summary (PDF)</li>
                </ul>
                <button 
                  onClick={handleDownloadBasicReport}
                  disabled={downloading}
                  className="w-full px-6 py-3 bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold"
                >
                  {downloading ? 'Generating PDF...' : 'Download Free Report'}
                </button>
              </div>

              <div className="border-2 border-emerald-500 dark:border-emerald-400 rounded-xl p-6 relative">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-emerald-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                    RECOMMENDED
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Detailed Analysis Report</h3>
                <div className="text-3xl font-bold text-emerald-600 mb-2">$2,000 AUD</div>
                <ul className="text-left text-gray-600 dark:text-gray-400 space-y-2 mb-6">
                  <li>✅ Everything in Basic Report, plus:</li>
                  <li>✅ Industry benchmarking & peer comparison</li>
                  <li>✅ Full ROI calculations & payback analysis</li>
                  <li>✅ Detailed 12-month implementation roadmap</li>
                  <li>✅ Best practice case studies</li>
                  <li>✅ 1-hour executive consultation call</li>
                </ul>
                <button 
                  onClick={handleDownloadDetailedReport}
                  disabled={downloadingDetail}
                  className="w-full px-6 py-3 bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold mb-3"
                >
                  {downloadingDetail 
                    ? 'Processing...' 
                    : isPaid 
                      ? 'Download Detailed Report' 
                      : 'Purchase Detailed Report'}
                </button>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Includes quantified ROI, benchmarking, and consultation
                </p>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Questions about your results? Contact our energy management experts.
              </p>
              <div className="flex justify-center space-x-4">
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Return to Dashboard
                </button>
                <button 
                  onClick={() => navigate('/emha')}
                  className="px-6 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600"
                >
                  Take Another Assessment
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Upgrade Modal */}
        {showUpgrade && (
          <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-80 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md mx-4">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Upgrade to Detailed Report</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Get comprehensive insights and a detailed implementation roadmap for $2,000 AUD.
              </p>
              <div className="flex space-x-4">
                <button 
                  onClick={() => setShowUpgrade(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => {
                    showWarning('Coming Soon', 'Payment processing coming soon!');
                    setShowUpgrade(false);
                  }}
                  className="flex-1 px-4 py-2 bg-emerald-600 dark:bg-emerald-700 text-white rounded-lg hover:bg-emerald-700 dark:hover:bg-emerald-600"
                >
                  Proceed to Payment
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EMHAResults;
